/* Built In Imports */
import dynamic from 'next/dynamic';
import { useContext, useEffect } from 'react';

/* External Imports */
import { Box, Heading } from '@chakra-ui/react';

/* Internal Imports */
/* Components */
import MidContainer from '@Layout/MidContainer';
import config from '@config';
import PageConfig from '@lb/Common/PageConfig';
import TopBannerDark from '@lb/components/Banners/TopBannerDark';
import BlogTopics from '@lb/components/Chips/BlogTopics';
import LingaBhairaviFacadeBlogListingPlaceholder from '@lb/components/Filter/LingaBhairaviFacadeBlogListingPlaceholder';
import SearchFilterBlog from '@lb/components/Filter/SearchFilterBlog';
import WrapLayout from '@lb/components/Layout/WrapLayout';
import Breadcrum from '@lb/components/Navigation/Breadcrum';
import HeadComponent from '@lb/components/Utility/HeadComponent';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@msr/components/Utility/Shared/SharedService';
import {
    fetchFooterDetailData,
    fetchHeaderMenuData,
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/lbService';
const ArticlesSubscribeBox = dynamic(() =>
  import('@lb/components/Subscribe/ArticlesSubscribeBox')
);

/**
 * @param {object} pageData
 * @param lang
 * @param region
 * @returns
 */

const BlogTopic = ({ pageData, region, lang }) => {
  const authContext = useContext(AuthContext);

  useEffect(() => {
    authContext.RegionAndLang(region, lang);
  }, [authContext, region, lang]);

  const newsLetterBlock = pageData?.customBlocks?.filter((block, i) => {
    return block._modelApiKey === 'newsletter_subscription_form';
  })[0];
  const filterBlock = pageData?.customBlocks?.filter((block, i) => {
    return block._modelApiKey === 'linga_bhairavi_filter_blog';
  })[0];
  const relatedTopics = pageData?.customBlocks?.filter((block, i) => {
    return (
      block._modelApiKey === 'linga_bhairavi_facade_blog_listing_placeholder'
    );
  })[0];
  let postListBlock = pageData?.customBlocks?.filter((block, i) => {
    return block._modelApiKey === 'linga_bhairavi_filter_blog';
  })[0];
  // to set load more true
  postListBlock.showLoadMoreButton = true;

  const topBannerDarkObj = {
    id: '146869981',
    _modelApiKey: 'top_banner_linga_bhairavi_dark',
    button: [],
    description: {
      value: {
        schema: 'dast',
        document: {
          type: 'root',
          children: [
            {
              type: 'blockquote',
              children: [
                {
                  type: 'paragraph',
                  children: [
                    {
                      type: 'span',
                      value:
                        'The intimate physical touch with Devi will change your energy system in such a way that Devi’s Grace will function within you, bringing about a profound transformation of Life.',
                    },
                  ],
                },
              ],
            },
          ],
        },
      },
    },
    desktopImage: {
      alt: null,
      title: null,
      url: `${config.imgPath}/d/46272/1684412365-blog-tag-result-page-desktop.jpg`,
      size: 179999
    },
    mobileImage: {
      url: `${config.imgPath}/d/46272/1684412486-blog-tag-result-page-mobile.jpg`,
      title: null,
      alt: null,
      size: 254070
    },
    quoteName: {
      value: {
        schema: 'dast',
        document: {
          type: 'root',
          children: [
            {
              type: 'paragraph',
              children: [
                {
                  type: 'span',
                  value: '—Sadhguru',
                },
              ],
            },
          ],
        },
      },
    },
    style: 'default',
    title: pageData.title,
    titleSubtext: ''
  };
  //? Hardcoded

  return (
    <WrapLayout pageData={pageData}>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData.availableLocales}
      >
        {' '}
      </HeadComponent>
      {pageData?.breadcrumbs?.length > 0 && (
        <Breadcrum
          page={pageData.breadcrumbs}
          alsoin={pageData.alsoin}
          url=""
          region={region}
          lang={lang}
        />
      )}
      <Box
        as="section"
        background={`url(${config.imgPath}/d/46272/1682095879-white-texture-final.png)`}
        className="container-article"
        p="0"
      >
        <TopBannerDark
          sectionContent={topBannerDarkObj}
          region={region}
          language={lang}
        />

        <SearchFilterBlog sectionContent={filterBlock} />

        <LingaBhairaviFacadeBlogListingPlaceholder
          loadedPosts={pageData?.posts?.cards}
          sectionContent={postListBlock}
          region={region}
          language={lang}
          tUuid={pageData.uuid}
        />

        <ArticlesSubscribeBox
          region={region}
          language={lang}
          sectionContent={newsLetterBlock}
        />

        <MidContainer w="1210px">
          {relatedTopics.topics?.length > 0 && (
            <Box
              w={'100%'}
              p={{ base: '70px 17px', lg: '60px 17px', xl: '60px 0' }}
            >
              <Heading
                as="h4"
                fontSize="26px"
                lineHeight="25px"
                color="#65000F"
                // mt="70px"
                // ml={{ base: '16px', md: 3, lg: 0 }}
                mb="24px"
                fontFamily="FedraSerifAStdBook"
                fontWeight="bold"
              >
                Explore topics of choice
              </Heading>
              <BlogTopics
                topics={relatedTopics.topics}
                region={region}
                lang={lang}
              />
            </Box>
          )}
        </MidContainer>
      </Box>
      <PageConfig isLikeVisible={false} pageData={pageData} />
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const lbPaths = config.USE_LOCAL_URL
    ? [
        `/linga-bhairavi/in/en/topic/devotion`,
        `/linga-bhairavi/in/en/topic/death`,
        `/linga-bhairavi/in/en/topic/bring-devi-home`,
      ]
    : await getLandingPaths('L2', '/topic/', 'linga_bhairavi_blog_topic');
  return {
    paths: lbPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, lang, topic },
}) => {
  const topMenuData = await fetchHeaderMenuData(
    getLocale(region, lang, 'lb'),
    'lbtopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale(region, lang, 'lb'),
    'lbmainmenu'
  );
  const footerData = await fetchFooterDetailData(
    getLocale(region, lang, 'lb'),
    'lbfooter'
  );
  const pageData = await fetchDetails(region, lang, `topic/${topic}`, {
    preview,
    data: previewData,
  });

  return {
    props: {
      headerMenuData: headerMenuData?.data || null,
      footerData: footerData?.data || null,
      topMenuData: topMenuData,
      pageData: pageData || null,
      isPreview: preview ? true : false,
      region: region,
      lang: lang,
      menuType: 'lb',
    },
  };
};

export default BlogTopic;
